import {
  ItemInfo,
  UploadSetwithTable$OnActivatedEvent,
  UploadSetwithTable$UploadCompletedEvent,
} from "sap/m/plugins/UploadSetwithTable";

import {
  GetButtonTypeForWorkOrderStatus,
  GetStateForWorkOrderStatus,
  GetTextKeyForWorkOrderStatusButton,
} from "akademika/akamont/utils/WorkOrderStatusUtil";

import { ContextWithPropertySetter } from "akademika/akamont/models/ContextWithPropertySetter";
import { ChangeWorkOrderStatus } from "akademika/akamont/utils/WorkOrderStatusChangeUtil";
import { WorkOrderItemActionTypes } from "../../constants/WorkOrderItemActionType";
import AttachmentController from "../sharedControllers/AttachmentController";
import ManagedAttachmentUtil from "../../utils/ManagedAttachmentUtil";
import { WorkOrderStatus } from "../../constants/WorkOrderStatuses";
import { AttachmentSelectionDto } from "../../models/AttachmentDto";
import { WorkOrderItemDto } from "../../models/UpdateWorkOrderDto";
import { IndicationColor, ValueState } from "sap/ui/core/library";
import { ComboBox$SelectionChangeEvent } from "sap/m/ComboBox";
import { Routes } from "akademika/akamont/constants/Routes";
import { IsUserInternal } from "../../services/AuthService";
import MessageType from "sap/ui/core/message/MessageType";
import { AppSettings } from "../../constants/AppSettings";
import JSONModel from "sap/ui/model/json/JSONModel";
import { Button$PressEvent } from "sap/m/Button";
import MessageToast from "sap/m/MessageToast";
import Dialog from "sap/m/Dialog";

/**
 * @namespace akademika.akamont.pages.workOrderDetailsPartner
 */
export default class WorkOrderDetailsPartner extends AttachmentController {
  route = Routes.WORK_ORDER_DETAILS_PARTNER;
  parentRoute = Routes.WORK_ORDER_LIST;
  navBackUsingHistoryFirst = true;

  addNewWorkOrderItemCommentUri = "api/workOrder/AddWorkOrderItemComment";

  private _commentDialog: Dialog;
  private _attachmentsDialog: Dialog;

  public async onRouteMatched() {
    this.destroyExistingDialogs();
    if (IsUserInternal()) {
      this.navTo(Routes.WORK_ORDER_DETAILS_INTERNAL, {
        workOrderId: this.routeParams.workOrderId,
      });
      return;
    }

    this.setModel(new JSONModel(), "workOrderComment");
    this.refreshWorkOrderDetails();

    this.fetchModel(
      "api/WorkOrder/GetServiceOptionsForPartner",
      "serviceOptions"
    );

    this.setModel(
      new JSONModel({
        edit: false,
      }),
      "viewOptions"
    );
  }

  private destroyExistingDialogs() {
    if (this._commentDialog) {
      this._commentDialog.destroy();
    }
    if (this._attachmentsDialog) {
      this._attachmentsDialog.destroy();
    }
    this._destoryExistingDialogs();
  }

  public initUploadPlugin(event: UploadSetwithTable$OnActivatedEvent) {
    const baseUrl = this.getApiService().getBaseUrl();
    const uploadEndpoint = `${baseUrl}${this.uploadAttachmentUri}`;
    const uploadPlugin = event.getParameter("oPlugin");
    uploadPlugin?.setUploadUrl(uploadEndpoint);
    console.log("Upload plugin initialized.");
  }

  public workOrderAttachmentValidationHandler(itemInfo: ItemInfo) {
    return this.handleAttachmentValidation(itemInfo);
  }

  public onWorkOrderAttachmentUploadCompleted(
    event: UploadSetwithTable$UploadCompletedEvent
  ) {
    this.handleUploadCompleted(event);
  }

  public async openWorkOrderItemAttachments(event: Button$PressEvent) {
    const source = event.getSource();
    const workOrderBindingContext = source.getBindingContext();
    const workOrderItemPath = workOrderBindingContext?.getPath();

    this._attachmentsDialog = await this.openDialog(
      "akademika.akamont.pages.sharedDialogs.AttachmentsDialog",
      true
    );
    this._attachmentsDialog.setModel(source.getModel());
    this._attachmentsDialog.bindElement({ path: workOrderItemPath ?? "" });
  }

  public getStateForWorkOrderStatus = GetStateForWorkOrderStatus;
  public getButtonTypeForWorkOrderStatus = GetButtonTypeForWorkOrderStatus;

  public formatTextForStatusButton(status: string) {
    return this.translate(GetTextKeyForWorkOrderStatusButton(status));
  }

  public async refreshWorkOrderDetails() {
    await this.fetchModel(
      "api/WorkOrder/GetNextWorkOrderStatuses/" + this.routeParams.workOrderId,
      "nextStatuses"
    );
    await this.fetchModel(
      "api/WorkOrder/GetWorkOrderDetailsForPartner/" +
      this.routeParams.workOrderId
    );
  }

  public async onEditWorkOrder() {
    const viewOptionsModel = this.getModel("viewOptions") as JSONModel;
    let editValue = viewOptionsModel.getProperty("/edit");
    viewOptionsModel.setProperty("/edit", !editValue);

    if (editValue) {
      await this.refreshWorkOrderDetails();
    }
  }

  public async changeStatus(newStatus: WorkOrderStatus) {
    ChangeWorkOrderStatus(
      this,
      newStatus,
      this.refreshWorkOrderDetails.bind(this)
    );
  }

  public async onSaveWorkOrder() {
    await this.whileBusy(async () => {
      this.triggerUploadAttachments();
      MessageToast.show(this.translate("UploadingAttachments"));
      await Promise.all(this._pendingUploadsQueue);
      MessageToast.show(this.translate("AttachmentsUploaded"));
      const workOrderDetails = this.getModel().getData();

      await this.post("api/WorkOrder/UpdateWorkOrderForPartner", {
        body: workOrderDetails,
      });
      this.showMessage("saveSuccess");
      await this.refreshWorkOrderDetails();
    })

    this.getModel("viewOptions").setProperty("/edit", false);
  }

  public async addWorkOrderItem() {
    const workOrderItems = this.getModel().getProperty(
      "/workOrderItems"
    ) as unknown[];
    workOrderItems.push({
      isAddedJustNow: true,
      serviceQuantityPlanned: 0,
      serviceQuantityPerformed: 0,
      existingItem: false,
    });
    this.getModel().setProperty("/workOrderItems", workOrderItems);
  }

  public async openWorkOrderItemCommentPopup(workOrderItem: WorkOrderItemDto) {
    const commentModel = new JSONModel({
      newComment: "",
      comments: workOrderItem.comments,
      workOrderItemId: workOrderItem.workOrderItemId,
    });

    this._commentDialog = await this.openDialog(
      "akademika.akamont.pages.workOrderDetailsInternal.WorkOrderItemCommentDialog",
      true
    );
    this._commentDialog.setModel(commentModel, "commentModel");
  }

  public async addWorkOrderItemComment() {
    const newComment = this._commentDialog
      .getModel("commentModel")
      ?.getProperty("/newComment");

    const workOrderItemId = this._commentDialog
      .getModel("commentModel")
      ?.getProperty("/workOrderItemId");

    if (newComment) {
      await this.saveDataAndShowMessage(this.addNewWorkOrderItemCommentUri, {
        params: {
          workOrderItemId,
          newComment,
        },
      });
      this.closeDialog();
      await this.refreshWorkOrderDetails();
    }
  }

  public async addWorkOrderComment() {
    const newComment = this.getModel("workOrderComment").getProperty("/value");
    const workOrderId = this.getModel().getProperty("/workOrderId");

    if (newComment) {
      await this.saveDataAndShowMessage("api/workOrder/AddWorkOrderComment", {
        params: {
          workOrderId: workOrderId,
          newComment,
        },
      });
      await this.refreshWorkOrderDetails();
    }
  }

  public disputeWoi(woiId: number) {
    this.openSimpleDialog({
      title: "disputeWorkOrderItem",
      message: "disputeWorkOrderItemQuestion",
      state: ValueState.Error,
      type: "Confirm",
      confirmWithComment: true,
      confirmAction: async (comment?: string) => {
        await this.post(`api/WorkOrder/DisputeWorkOrderItem/${woiId}`, {
          params: {
            newComment: comment,
            actionName: this.translate(WorkOrderItemActionTypes.DISPUTE),
          },
        });
        await this.refreshWorkOrderDetails();
      },
      cancelAction: async () => await this.refreshWorkOrderDetails(),
    });
  }

  public flagUncompletedWoi(woiId: number) {
    this.openSimpleDialog({
      title: "flagUncompletedWorkOrderItem",
      message: "flagUncompletedWorkOrderItemQuestion",
      state: ValueState.Warning,
      type: "Confirm",
      confirmWithComment: true,
      confirmAction: async (comment?: string) => {
        await this.post(`api/WorkOrder/FlagWorkOrderItemUncompleted/${woiId}`, {
          params: {
            newComment: comment,
            actionName: this.translate(WorkOrderItemActionTypes.UNCOMPLETED),
          },
        });
        await this.refreshWorkOrderDetails();
      },
      cancelAction: async () => await this.refreshWorkOrderDetails(),
    });
  }

  public onExportPdf() {
    const workOrderId = this.routeParams.workOrderId;
    this.navTo(Routes.WORK_ORDER_TO_PDF, { workOrderId });
  }

  public getWorkOrderItemHighlight(
    deviation: string
  ): MessageType | IndicationColor {
    switch (deviation) {
      case "workOrderItemCorrected":
        return MessageType.Error;
      case "workOrderItemAddedByInternalUser":
        return MessageType.Warning;

      default:
        return MessageType.None;
    }
  }

  public onServiceMeasurementUnitSelectedForItem(
    event: ComboBox$SelectionChangeEvent
  ) {
    const selectedItem = event.getParameter("selectedItem");
    if (!selectedItem) return;

    const workOrderItemContext =
      selectedItem.getBindingContext() as ContextWithPropertySetter;

    const serviceOption = selectedItem
      .getBindingContext("serviceOptions")
      ?.getProperty("");

    workOrderItemContext.setProperty(
      "isSingleQuantity",
      serviceOption.isSingleQuantity
    );
    workOrderItemContext.setProperty(
      "measurementUnitNameTranslationKey",
      serviceOption.measurementUnitNameTranslationKey
    );
    if (serviceOption.isSingleQuantity) {
      workOrderItemContext.setProperty("serviceQuantityPerformed", 1);
    }
  }

  public handleAttachmentPreview = async (attachment: AttachmentSelectionDto, sourceType: AttachmentSelectionDto["sourceType"]) => {
    if (sourceType) attachment.sourceType = sourceType;
    await new ManagedAttachmentUtil(this, attachment).handlePreview();
  }

  public handleAttachmentPreviewEnabled(mimetype: string) {
    return AppSettings.files.supportedPreviewTypes.includes(mimetype);
  }
}
